/**
 * What changed when a task save is refused for a version conflict (#1757, FM7 of #1697).
 *
 * The edit form keeps the values it was filled from (its base). On a conflict the portal reads the task again and
 * compares, field by field: the fields the newer version changed from the base are "theirs"; a field this person
 * changed too, to something else, is changed on both sides. Fields are taken whole, lists and long text included,
 * never merged line by line (#1427), and where both sides changed a field the person picks: there is no default.
 */

/** The task edit form's values: its text fields, and the rows of its list editors. */
export interface TaskEditorValues<F extends object = Record<string, unknown>> {
    form: F
    inputs: unknown[]
    gates: unknown[]
    successors: unknown[]
    /** The tool slots it declares. Absent (a draft stored before the form had them) reads as none. */
    toolSlots?: unknown[]
}

/** A field a conflict can name, by its label on the form. */
export interface ConflictFieldDef {
    key: string
    label: string
}

/** Every field the edit form saves, in form order. Lists are the list keys of `TaskEditorValues`. */
export const CONFLICT_FIELDS: readonly ConflictFieldDef[] = [
    { key: 'name', label: 'Name' },
    { key: 'slug', label: 'Slug' },
    { key: 'kind', label: 'Kind' },
    { key: 'status', label: 'Status' },
    { key: 'teamId', label: 'Team' },
    { key: 'assigneeRole', label: 'Role' },
    { key: 'spendUsd', label: 'Spend per run' },
    { key: 'model', label: 'Model' },
    { key: 'goal', label: 'Goal' },
    { key: 'metric', label: 'Metrics' },
    { key: 'guardrails', label: 'Guardrails' },
    { key: 'tools', label: 'Tools' },
    { key: 'optionalTools', label: 'Optional tools' },
    { key: 'toolSlots', label: 'Tool slots' },
    { key: 'inputs', label: 'Inputs' },
    { key: 'gates', label: 'Gates' },
    { key: 'workspace', label: 'Workspace JSON' },
    { key: 'successors', label: 'Successors' },
    { key: 'finishesOn', label: 'Finishes the work on' },
    { key: 'mailboxActionOn', label: 'Mailbox button' },
    { key: 'mailboxActionLabel', label: 'Mailbox button label' },
    { key: 'skillMd', label: 'SKILL.md' },
]

type ListKey = 'inputs' | 'gates' | 'successors' | 'toolSlots'
const LIST_KEYS = new Set<string>(['inputs', 'gates', 'successors', 'toolSlots'] satisfies ListKey[])

/** A field the newer version changed, with both sides' values. */
export interface ConflictField extends ConflictFieldDef {
    mine: unknown
    theirs: unknown
    /** This person changed it too, to something else: they must pick a side before a merge completes. */
    both: boolean
}

/** Which side a field changed on both sides takes. */
export type ConflictChoice = 'mine' | 'theirs'

/** A copy that shares nothing with the reactive form it came from. */
export function cloneValues<T>(values: T): T {
    return JSON.parse(JSON.stringify(values)) as T
}

function fieldValue(values: TaskEditorValues<object>, key: string): unknown {
    if (LIST_KEYS.has(key)) return values[key as ListKey] ?? []
    return (values.form as Record<string, unknown>)[key]
}

function same(left: unknown, right: unknown): boolean {
    return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

/**
 * The fields `theirs` changed from `base`, each marked when `mine` changed it too, to a different value.
 *
 * With no `base` (a draft stored before the form kept what it was filled from), no field can be told apart as changed
 * only by the newer version: every field where `mine` and `theirs` differ is marked as changed on both sides, so a
 * merge asks for a side on each rather than overwriting an edit.
 */
export function conflictFields(
    base: TaskEditorValues<object> | null,
    mine: TaskEditorValues<object>,
    theirs: TaskEditorValues<object>,
): ConflictField[] {
    const from = base ?? mine
    return CONFLICT_FIELDS.filter(field => !same(fieldValue(theirs, field.key), fieldValue(from, field.key))).map(
        field => {
            const mineValue = fieldValue(mine, field.key)
            const theirsValue = fieldValue(theirs, field.key)
            return {
                ...field,
                mine: mineValue,
                theirs: theirsValue,
                both: base === null || (!same(mineValue, fieldValue(base, field.key)) && !same(mineValue, theirsValue)),
            }
        },
    )
}

/** True when `mine` differs from `base` in any field the form saves: there are edits a Reload would drop. */
export function hasOwnEdits(base: TaskEditorValues<object>, mine: TaskEditorValues<object>): boolean {
    return CONFLICT_FIELDS.some(field => !same(fieldValue(mine, field.key), fieldValue(base, field.key)))
}

/** True once every field changed on both sides has a choice. */
export function mergeReady(fields: ConflictField[], choices: Record<string, ConflictChoice>): boolean {
    return fields.every(field => !field.both || choices[field.key] !== undefined)
}

/**
 * This person's values on top of the newer version: every field only the newer version changed is taken from it,
 * and a field both sides changed takes the side chosen for it. Fields only this person changed stay theirs.
 */
export function mergeValues<T extends TaskEditorValues<object>>(
    mine: T,
    fields: ConflictField[],
    choices: Record<string, ConflictChoice>,
): T {
    const merged = cloneValues(mine)
    for (const field of fields) {
        if (field.both && choices[field.key] !== 'theirs') continue
        const value = cloneValues(field.theirs ?? null)
        if (LIST_KEYS.has(field.key)) {
            merged[field.key as ListKey] = (value ?? []) as unknown[]
        } else {
            const form = merged.form as Record<string, unknown>
            form[field.key] = value ?? ''
        }
    }
    return merged
}

/** A field's value as the conflict panel shows it: text as typed, a list as its JSON. */
export function shownValue(value: unknown): string {
    if (typeof value === 'string') return value === '' ? '(empty)' : value
    if (Array.isArray(value) && value.length === 0) return '(none)'
    return JSON.stringify(value ?? null, null, 2)
}
